// ChangePasswordModal.jsx - Modal for password change and reset
import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dialog, DialogHeader } from "@astryxdesign/core/Dialog";
import { Stack, HStack, StackItem } from "@astryxdesign/core/Stack";
import { TabList, Tab } from "@astryxdesign/core/TabList";
import { TextInput } from "@astryxdesign/core/TextInput";
import { IconButton } from "@astryxdesign/core/IconButton";
import { Button } from "@astryxdesign/core/Button";
import { Banner } from "@astryxdesign/core/Banner";
import { authAPI } from "../../services/api";

/**
 * ChangePasswordModal Component
 * Modal with two tabs:
 * 1. Change password (when logged in)
 * 2. Request password reset via email
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Modal open state
 * @param {Function} props.onClose - Function to close modal
 */
const ChangePasswordModal = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState("change"); // "change" or "reset"
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Form states for change password
  const [changePasswordData, setChangePasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // Form states for reset password
  const [resetPasswordData, setResetPasswordData] = useState({
    email: "",
  });

  // Show/hide password states
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setActiveTab("change");
      setChangePasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setResetPasswordData({ email: "" });
      setError("");
      setSuccess("");
      setShowPasswords({ current: false, new: false, confirm: false });
    }
  }, [isOpen]);

  // Clear errors after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Clear success after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const setChangeField = (name) => (value) => {
    setChangePasswordData((prev) => ({ ...prev, [name]: value }));
    if (error) setError("");
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const validateChangePassword = () => {
    if (!changePasswordData.currentPassword) {
      setError("Senha atual é obrigatória");
      return false;
    }
    if (!changePasswordData.newPassword) {
      setError("Nova senha é obrigatória");
      return false;
    }
    if (changePasswordData.newPassword.length < 6) {
      setError("A nova senha deve ter pelo menos 6 caracteres");
      return false;
    }
    if (changePasswordData.newPassword !== changePasswordData.confirmPassword) {
      setError("As senhas não coincidem");
      return false;
    }
    if (changePasswordData.currentPassword === changePasswordData.newPassword) {
      setError("A nova senha deve ser diferente da senha atual");
      return false;
    }
    return true;
  };

  const validateResetPassword = () => {
    if (!resetPasswordData.email) {
      setError("Email é obrigatório");
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(resetPasswordData.email)) {
      setError("Por favor, digite um email válido");
      return false;
    }
    return true;
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!validateChangePassword()) {
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.changePassword(
        changePasswordData.currentPassword,
        changePasswordData.newPassword
      );

      if (response.success) {
        setSuccess("Senha alterada com sucesso!");
        // Reset form
        setChangePasswordData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
        // Close modal after 2 seconds
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setError(response.message || "Erro ao alterar senha");
      }
    } catch (error) {
      console.error("Change password error:", error);
      setError(
        error.message || "Erro ao conectar com o servidor. Tente novamente."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!validateResetPassword()) {
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.requestPasswordReset(
        resetPasswordData.email
      );

      if (response.success) {
        setSuccess(
          "Se o email estiver cadastrado, você receberá um link para redefinir sua senha."
        );
        // Reset form
        setResetPasswordData({ email: "" });
        // Close modal after 3 seconds
        setTimeout(() => {
          onClose();
        }, 3000);
      } else {
        setError(response.message || "Erro ao solicitar reset de senha");
      }
    } catch (error) {
      console.error("Request password reset error:", error);
      setError(
        error.message || "Erro ao conectar com o servidor. Tente novamente."
      );
    } finally {
      setLoading(false);
    }
  };

  // Don't render if modal is not open
  if (!isOpen) return null;

  // Campo de senha com botão de mostrar/ocultar (padrão do Login)
  const passwordField = (label, name, field, placeholder, autoComplete) => (
    <HStack gap={2} vAlign="end">
      <StackItem size="fill">
        <TextInput
          label={label}
          type={showPasswords[field] ? "text" : "password"}
          htmlName={name}
          placeholder={placeholder}
          autoComplete={autoComplete}
          value={changePasswordData[name]}
          onChange={setChangeField(name)}
        />
      </StackItem>
      <IconButton
        label={showPasswords[field] ? "Ocultar senha" : "Mostrar senha"}
        tooltip={showPasswords[field] ? "Ocultar senha" : "Mostrar senha"}
        icon={<FontAwesomeIcon icon={showPasswords[field] ? "eye-slash" : "eye"} />}
        variant="ghost"
        onClick={() => togglePasswordVisibility(field)}
      />
    </HStack>
  );

  return (
    <Dialog
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      width={440}
    >
      <Stack gap={3}>
        <DialogHeader
          title="Redefinição de Senha"
          onOpenChange={(open) => {
            if (!open) onClose();
          }}
          hasDivider
        />

        <TabList
          label="Modo de redefinição"
          value={activeTab}
          onChange={(v) => {
            setActiveTab(v);
            setError("");
            setSuccess("");
          }}
          layout="fill"
        >
          <Tab
            value="change"
            label="Alterar Senha"
            icon={<FontAwesomeIcon icon="lock" />}
          />
          <Tab
            value="reset"
            label="Esqueci Minha Senha"
            icon={<FontAwesomeIcon icon="envelope" />}
          />
        </TabList>

        {success && <Banner status="success" title={success} />}
        {error && <Banner status="error" title={error} />}

        {activeTab === "change" ? (
          <form onSubmit={handleChangePassword}>
            <Stack gap={3}>
              {passwordField(
                "Senha Atual",
                "currentPassword",
                "current",
                "Digite sua senha atual",
                "current-password",
              )}
              {passwordField(
                "Nova Senha",
                "newPassword",
                "new",
                "Digite a nova senha (mín. 6 caracteres)",
                "new-password",
              )}
              {passwordField(
                "Confirmar Nova Senha",
                "confirmPassword",
                "confirm",
                "Confirme a nova senha",
                "new-password",
              )}
              <Button
                label={loading ? "Alterando..." : "Alterar Senha"}
                variant="primary"
                type="submit"
                icon={<FontAwesomeIcon icon="save" />}
                isLoading={loading}
              />
            </Stack>
          </form>
        ) : (
          <form onSubmit={handleRequestReset}>
            <Stack gap={3}>
              <Banner
                status="info"
                title="Digite seu email cadastrado. Você receberá um link para redefinir sua senha."
              />
              <TextInput
                label="Email"
                type="email"
                htmlName="email"
                placeholder="Digite seu email"
                autoComplete="email"
                value={resetPasswordData.email}
                onChange={(v) => {
                  setResetPasswordData({ email: v });
                  if (error) setError("");
                }}
              />
              <Button
                label={loading ? "Enviando..." : "Enviar Link de Reset"}
                variant="primary"
                type="submit"
                icon={<FontAwesomeIcon icon="paper-plane" />}
                isLoading={loading}
              />
            </Stack>
          </form>
        )}
      </Stack>
    </Dialog>
  );
};

export default ChangePasswordModal;
